// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { runInAction, toJS } from "mobx";
import React from "react";

import { Opportunity } from "../../../../WorkflowsStore";
import {
  packDraftData,
  UsIaEarlyDischargeDraftData,
} from "../../../../WorkflowsStore/Opportunity/Forms/UsIaEarlyDischargeForm";
import { UsIaEarlyDischargeOpportunity } from "../../../../WorkflowsStore/Opportunity/UsIa";
import { FileGeneratorArgs, renderMultipleDocx } from "../../DOCXFormGenerator";
import { FormContainer } from "../../FormContainer";
import FormViewer from "../../FormViewer";
import { downloadZipFile } from "../../utils";
import cbcDischargeReportTemplate from "./cbc_discharge_report_template.docx";
import { CbcDischargeReport } from "./CbcDischargeReport";
import probationDischargeOrderTemplate from "./probation_discharge_order_template.docx";
import { ProbationDischargeOrder } from "./ProbationDischargeOrder";

const formDownloader = async (
  opportunity: UsIaEarlyDischargeOpportunity,
): Promise<void> => {
  let contents: Partial<UsIaEarlyDischargeDraftData> = {};
  // we are not mutating any observables here, just telling Mobx not to track this access
  runInAction(() => {
    contents = {
      ...toJS(opportunity?.form?.formData),
    };
  });

  // @ts-expect-error We know the draft data is fully defined
  contents = packDraftData(contents);

  const client = opportunity.person;

  const fileInputs: FileGeneratorArgs[] = [
    [
      `${client.displayName} - CBC Discharge Report.docx`,
      cbcDischargeReportTemplate,
      contents,
    ],
    [
      `${client.displayName} - Proposed Probation Discharge Order.docx`,
      probationDischargeOrderTemplate,
      contents,
    ],
  ];

  downloadZipFile(`${client?.displayName} Probation Discharge Packet.zip`, [
    ...(await renderMultipleDocx(fileInputs)),
  ]);
};

export function FormUsIaEarlyDischargeProbation({
  opportunity,
}: {
  opportunity: Opportunity;
}) {
  const formRef = React.useRef<HTMLDivElement>(null);

  if (!(opportunity instanceof UsIaEarlyDischargeOpportunity)) {
    return null;
  }

  return (
    <FormContainer
      heading={"Early Discharge from Probation"}
      agencyName={"IDOC"}
      downloadButtonLabel={"Download Form"}
      onClickDownload={() => formDownloader(opportunity)}
      opportunity={opportunity}
    >
      <FormViewer formRef={formRef}>
        <CbcDischargeReport />
        <ProbationDischargeOrder />
      </FormViewer>
    </FormContainer>
  );
}
